import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { ModalContent } from '@src/shared/modal/confirm-modal/confirm-modal.type';
import { ModalService } from '@src/shared/modal/modal.service';
import { SubscriptionHandler } from '../../../advanced_campaign_booking.module/components/subscription-handler';
import { CampaignMmsGainFormData } from '../../../woo_components.module/campaign_mms_gain_form/campaign-mms-gain-form.component';
import { CampaignService } from '../../../woo_services.module/CampaignService';
import { DashboardModel, DashboardStore } from '../../../woo_services.module/DashboardStore';
import { TranslationService } from '../../../woo_services.module/TranslationService';
import {
  Campaign,
  CampaignStatus,
  DashboardTab,
  GoalUpdateBookingError,
  UpdateGoalsJobParams,
  UpdateGoalsJobResult,
  UpdateGoalsJobTargetingData,
  wooId,
} from '../../../woo_services.module/shared-types';

@Component({
  selector: 'campaign-goals',
  templateUrl: './campaign-goals.component.html',
  standalone: false,
})
export class CampaignGoalsComponent extends SubscriptionHandler implements OnDestroy {
  campaign: Campaign;
  editGoals = false;
  canSave: boolean;
  hasChanges: boolean;
  statusDisabled: boolean;

  constructor(
    private campaignService: CampaignService,
    private dashboardStore: DashboardStore,
    private modalService: ModalService,
    private translationService: TranslationService,
  ) {
    super();
    this.dashboardStore.state$.subscribe(this.initFromStore);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.cancelEditGoals();
  }

  initFromStore = (model: DashboardModel): void => {
    if (model.activeCampaign) {
      this.campaign = model.activeCampaign;
      this.statusDisabled =
        model.activeCampaign.status !== CampaignStatus.upcoming &&
        model.activeCampaign.status !== CampaignStatus.ongoing;
    }

    this.editGoals = model.editGoals;
    this.canSave = model.modifiedGoals ? this.combineValidityStatesFromStore() : false;
    this.hasChanges = model.modifiedGoals ? this.combineModifiedStatesFromStore() : false;
  };

  startEditGoals = (): void => {
    this.dashboardStore.setEditGoals(true);
  };

  cancelEditGoals = (): void => {
    this.dashboardStore.setEditGoals(false);
  };

  saveEditGoals = (): void => {
    if (!this.canSave) {
      return;
    }

    if (!this.hasChanges) {
      this.modalService.openSuccess('Det här gick lättare än förväntat, inga ändringar att spara :)');
      this.dashboardStore.setEditGoals(false);
    } else {
      this.dialogUpdateGoals();
    }
  };

  onMmsFormChange = (mmsFormData: CampaignMmsGainFormData): void => {
    const mergedMmsFormValues = {
      ...this.dashboardStore.state.modifiedGoals?.mmsForm.values,
      ...mmsFormData,
    };

    this.dashboardStore.setModifiedGoalsMmsFormValues(mergedMmsFormValues);
  };

  onMmsFormValidityChange = (valid: boolean): void => {
    this.dashboardStore.setModifiedGoalsMmsFormValidity(valid);
  };

  getTargetingExpandedFromStore = (targetingId: wooId): boolean => {
    const expandedTargetings = this.dashboardStore.state.expandedTargetingHeaders?.[DashboardTab.goals];
    return expandedTargetings?.[targetingId] ?? true;
  };

  onTargetingHeaderExpandedChange = (expanded: boolean, targetingId: wooId): void => {
    this.dashboardStore.setExpandedTargetingHeader(DashboardTab.goals, targetingId, expanded);
  };

  private dialogUpdateGoals = () => {
    const successMessage = 'WOO har matat in de extra visningarna i sitt lager';
    const updateParams = this.convertModifiedGoalsToParams(this.dashboardStore.state.modifiedGoals);
    let continueEditingAfterSumbit = false;

    this.updateGoals(updateParams)
      .then(
        () => {
          this.modalService.openSuccess(`${successMessage}.`);
        },
        (res) => {
          if (res.booking_error === GoalUpdateBookingError.goalTotalImpressionsBelowZero) {
            continueEditingAfterSumbit = true;
          }

          if (!updateParams.force && res.booking_error === GoalUpdateBookingError.insufficientInventory) {
            this.modalService.openConfirm(this.getConfirmOverbookingDialogContent()).then(
              (response) => {
                if (!response.confirm) return;

                updateParams.force = true;
                this.updateGoals(updateParams)
                  .then(() => {
                    this.modalService.openSuccess(`${successMessage}, men är nu överbokat.`);
                  })
                  .catch((err) => {
                    this.modalService.openError(
                      res.booking_error,
                      this.translationService.convertError(err.error.error),
                    );
                    continueEditingAfterSumbit = true;
                  });
              },
              () => {
                continueEditingAfterSumbit = true;
              },
            );
          } else {
            throw res;
          }
        },
      )
      .catch(this.showBookingError)
      .finally(() => {
        if (!continueEditingAfterSumbit) {
          this.dashboardStore.setEditGoals(false);
          this.reloadCampaignFromServer(updateParams.campaign_id);
        }
      });
  };

  private updateGoals = (updateParams: UpdateGoalsJobParams) => {
    const loaderModal = this.modalService.openLoader('Uppdaterar WOOs lager');
    return this.campaignService.updateGoals(updateParams).then(
      (response) => {
        loaderModal.close();
        return response;
      },
      (error: UpdateGoalsJobResult) => {
        loaderModal.close();
        throw error;
      },
    );
  };

  private getConfirmOverbookingDialogContent = (): ModalContent => ({
    title: 'Varning!',
    texts: [
      `${this.translationService.convertUpdateGoalsError(GoalUpdateBookingError.insufficientInventory)}`,
      'Är du säker på att du vill överboka?',
    ],
    confirmLabel: 'Överboka lagret',
    cancelLabel: 'Avbryt',
  });

  private showBookingError = (error: UpdateGoalsJobResult) => {
    if (error.booking_error && !(error instanceof HttpErrorResponse)) {
      this.modalService.openError(this.translationService.convertUpdateGoalsError(error.booking_error));
    }
  };

  private reloadCampaignFromServer = (id: wooId = this.campaign.id) => {
    this.campaignService.get(id).then((campaign) => {
      this.dashboardStore.setActiveCampaign(campaign);
    });
  };

  private combineValidityStatesFromStore = (): boolean => {
    const mmsForm = this.dashboardStore.state.modifiedGoals.mmsForm.valid;
    const targetingForms = Object.values(this.dashboardStore.state.modifiedGoals.targetingForms).every((t) => t.valid);
    const goalForms = Object.values(this.dashboardStore.state.modifiedGoals.goalForms)
      .flatMap((t) => Object.values(t).map((g) => g.valid))
      .every((value) => value);
    return mmsForm && targetingForms && goalForms;
  };

  private combineModifiedStatesFromStore = (): boolean => {
    const mmsForm = this.dashboardStore.state.modifiedGoals.mmsForm.modified;
    const targetingForms = Object.values(this.dashboardStore.state.modifiedGoals.targetingForms).some(
      (t) => t.modified,
    );
    const goalForms = Object.values(this.dashboardStore.state.modifiedGoals.goalForms)
      .flatMap((t) => Object.values(t).map((g) => g.modified))
      .some((value) => value);
    return mmsForm || targetingForms || goalForms;
  };

  private convertModifiedGoalsToParams = (
    modifiedGoals: DashboardModel['modifiedGoals'],
    force = false,
  ): UpdateGoalsJobParams => {
    const targetings: UpdateGoalsJobTargetingData[] = Object.entries(modifiedGoals.targetingForms).map(
      ([targetingId, targetingForm]) => ({
        id: targetingId,
        additional_goal_budget: targetingForm.values.additionalGoalBudget,
        additional_goal_budget_message: targetingForm.values.additionalGoalBudgetMessage,
        goals: Object.entries(modifiedGoals.goalForms[targetingId]).map(([goalId, goalForm]) => ({
          id: goalId,
          additional_impressions: goalForm.values.additionalImpressions,
        })),
      }),
    );

    return {
      campaign_id: this.campaign.id,
      force: force,
      mms_gain: modifiedGoals.mmsForm.values.mmsGain,
      targetings: targetings,
    };
  };
}
